/**
 * Esquemas de tareas: lo que la API acepta (entradas) y lo que devuelve (respuestas).
 */
import { z } from 'zod';
import {
  DETALLE_DESCARTE_MINIMO,
  ESTADOS,
  ESTADOS_DEL_FLUJO,
  ESFUERZOS,
  MOTIVOS_DESCARTE,
  PRIORIDADES,
  TIPOS,
  TIPOS_EVENTO,
  errorFechaInicio,
  errorFechaLimite,
} from './dominio';
import { booleanoDeTexto, enlaceHttpSchema, fechaIsoSchema, idSchema } from './comunes';
import { proyectoResumenSchema } from './proyectos';
import { usuarioResumenSchema } from './usuarios';

/* ----------------------------- Entradas ----------------------------- */

/**
 * Campos editables de una tarea, sin valores por defecto.
 * Se separan de `crearTareaSchema` a propósito: en Zod, un campo con `.default()` se rellena
 * aunque se haga `.partial()`, y una edición parcial pisaría con valores por defecto los campos
 * que la persona no tocó.
 */
const fechaElegidaSchema = z
  .string()
  .trim()
  .superRefine((texto, ctx) => {
    const error = errorFechaInicio(texto);
    if (error) ctx.addIssue({ code: 'custom', message: error });
  });

/**
 * Fecha límite (CDT-76): día `AAAA-MM-DD` sin hora (se guarda como `DATE`), o `null` para quitarla. Es parte del
 * requerimiento (`CAMPOS_REQUERIMIENTO`): solo la cambian quien creó la tarea y los administradores.
 */
export const fechaLimiteSchema = z
  .string()
  .trim()
  .superRefine((texto, ctx) => {
    const error = errorFechaLimite(texto);
    if (error) ctx.addIssue({ code: 'custom', message: error });
  })
  .nullable();

/**
 * Motivo al descartar una tarea (ADR-0017). Va junto con `estado: 'DESCARTADA'` en `mover` o en el PATCH.
 *  - `motivo`: categoría (No por ahora, No es viable, Duplicada, Otro).
 *  - `detalle`: texto libre; obligatorio (mín. 5 caracteres) salvo en «Duplicada».
 *  - `duplicadaDe`: número de la tarea original (obligatorio en «Duplicada», que la API revisa que exista).
 */
export const descarteSchema = z
  .object({
    motivo: z.enum(MOTIVOS_DESCARTE, { error: 'Elige un motivo para descartar la tarea.' }),
    detalle: z.string().trim().max(1000).default(''),
    duplicadaDe: z.number().int().positive().nullable().default(null),
  })
  .superRefine((descarte, ctx) => {
    if (descarte.motivo === 'DUPLICADA') {
      if (descarte.duplicadaDe === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['duplicadaDe'],
          message: 'Indica la tarea original, por ejemplo CDT-12.',
        });
      }
    } else if (descarte.detalle.length < DETALLE_DESCARTE_MINIMO) {
      ctx.addIssue({
        code: 'custom',
        path: ['detalle'],
        message: 'Explica brevemente por qué se descarta (al menos 5 caracteres).',
      });
    }
  });
export type DescarteEntrada = z.infer<typeof descarteSchema>;

const camposTarea = {
  titulo: z.string().trim().min(1, 'El título es obligatorio.').max(200),
  descripcion: z.string().trim().max(5000),
  criterio: z.string().trim().max(2000),
  tipo: z.enum(TIPOS),
  prioridad: z.enum(PRIORIDADES),
  esfuerzo: z.enum(ESFUERZOS),
  estado: z.enum(ESTADOS),
  responsableId: z.number().int().positive().nullable(),
  pedidoPor: z.string().trim().max(80),
  tocaRiego: z.boolean(),
  requiereProduccion: z.boolean(),
  detalleProduccion: z.string().trim().max(300),
  enlace: enlaceHttpSchema,
  /** Proyecto del catálogo, o `null` si la tarea no tiene («Sin proyecto»). */
  proyectoId: z.number().int().positive().nullable(),
  /**
   * Fecha de inicio de la tarea (`creadoEn`): `AAAA-MM-DD` o ISO con hora. Solo un ADMIN puede darla
   * (ver `CAMPOS_SOLO_ADMIN`). No es futura ni anterior a 2000-01-01. La API la guarda como mediodía UTC
   * del día elegido.
   */
  creadoEn: fechaElegidaSchema,
  /**
   * Fecha de cierre (`cerradaEn`), mismo formato y regla de permisos. Solo en tareas «Hecho» y no anterior
   * a la fecha de inicio (lo revisa la API, que conoce la tarea).
   */
  cerradaEn: fechaElegidaSchema,
  /** Motivo del descarte: solo junto con `estado: 'DESCARTADA'` (si no, la API responde 400). */
  descarte: descarteSchema,
  /**
   * Tarea madre (ADR-0018), o `null` si no es subtarea. Un solo nivel: la madre no puede ser subtarea de otra, una
   * tarea con subtareas no puede ser subtarea y no puede ser la misma tarea (400 `padre_invalido`). Es parte del
   * requerimiento: solo la cambian quien creó la subtarea y los administradores.
   */
  padreId: z.number().int().positive().nullable(),
  /** Fecha límite `AAAA-MM-DD` o `null` (sin fecha). Parte del requerimiento. */
  fechaLimite: fechaLimiteSchema,
};

/** Campos que se pueden dar al crear una tarea. Solo el título es obligatorio. */
export const crearTareaSchema = z.object({
  titulo: camposTarea.titulo,
  descripcion: camposTarea.descripcion.default(''),
  criterio: camposTarea.criterio.default(''),
  tipo: camposTarea.tipo.default('REQUERIMIENTO'),
  prioridad: camposTarea.prioridad.default('P2'),
  esfuerzo: camposTarea.esfuerzo.default('MEDIA'),
  // Una tarea no nace descartada: se descarta después, con su motivo.
  estado: z.enum(ESTADOS_DEL_FLUJO).default('NUEVO'),
  responsableId: camposTarea.responsableId.default(null),
  pedidoPor: camposTarea.pedidoPor.default(''),
  tocaRiego: camposTarea.tocaRiego.default(false),
  requiereProduccion: camposTarea.requiereProduccion.default(false),
  detalleProduccion: camposTarea.detalleProduccion.default(''),
  enlace: camposTarea.enlace.default(''),
  proyectoId: camposTarea.proyectoId.default(null),
  padreId: camposTarea.padreId.default(null),
  fechaLimite: camposTarea.fechaLimite.default(null),
  // Sin default a propósito: si no viene, la tarea nace «ahora».
  creadoEn: camposTarea.creadoEn.optional(),
  cerradaEn: camposTarea.cerradaEn.optional(),
});
export type CrearTareaEntrada = z.infer<typeof crearTareaSchema>;

/** Edición parcial: solo se envían los campos que cambian. */
export const actualizarTareaSchema = z
  .object(camposTarea)
  .partial()
  .refine((datos) => Object.keys(datos).length > 0, {
    message: 'Indica al menos un campo para actualizar.',
  });
export type ActualizarTareaEntrada = z.infer<typeof actualizarTareaSchema>;

/** Cambio de columna en el tablero. Para descartar, `descarte` es obligatorio (lo revisa la API). */
export const moverTareaSchema = z.object({
  estado: z.enum(ESTADOS),
  descarte: descarteSchema.optional(),
});
export type MoverTareaEntrada = z.infer<typeof moverTareaSchema>;

/**
 * Bloqueo nuevo (ADR-0018): la tarea de la URL queda «bloqueada por» `bloqueanteId`. Es colaboración: lo agrega
 * cualquiera con escritura. La API rechaza: la misma tarea o la madre/hija de la tarea (400 `bloqueo_invalido`), un
 * bloqueante inexistente o eliminado (400 `bloqueo_invalido`), un bloqueo repetido (409 `bloqueo_existente`) y uno
 * que cierre un ciclo, directo o indirecto (400 `bloqueo_ciclo`).
 */
export const agregarBloqueoSchema = z.object({
  bloqueanteId: z.number().int().positive(),
});
export type AgregarBloqueoEntrada = z.infer<typeof agregarBloqueoSchema>;

/** Nota que se agrega al historial de una tarea. */
export const agregarNotaSchema = z.object({
  texto: z.string().trim().min(1, 'La nota no puede estar vacía.').max(1000),
});
export type AgregarNotaEntrada = z.infer<typeof agregarNotaSchema>;

/**
 * Filtros de la lista, tal como llegan en la URL (`?estado=EN_CURSO,POR_HACER&tocaRiego=true`).
 * Los valores múltiples van separados por coma.
 */
function listaDeValores<const T extends readonly [string, ...string[]]>(valores: T) {
  return z
    .string()
    .transform((texto) =>
      texto
        .split(',')
        .map((parte) => parte.trim())
        .filter(Boolean),
    )
    .pipe(z.array(z.enum(valores)));
}

export const filtrosTareasSchema = z.object({
  /**
   * Búsqueda por palabras (título, descripción, «pedido por» y responsable; «CDT-12» o «12» busca por número).
   * Con `palabras=todas` (por defecto) cada palabra debe aparecer en algún campo; con `alguna`, basta una
   * (lo usa «Tareas parecidas» al crear). Sin distinguir mayúsculas; las tildes sí cuentan.
   */
  q: z.string().trim().max(200).optional(),
  palabras: z.enum(['todas', 'alguna']).default('todas'),
  estado: listaDeValores(ESTADOS).optional(),
  tipo: listaDeValores(TIPOS).optional(),
  prioridad: listaDeValores(PRIORIDADES).optional(),
  /** Un id de persona, o `sin-asignar` para las tareas sin responsable. */
  responsable: z.union([z.literal('sin-asignar'), idSchema]).optional(),
  /** Un id de proyecto, o `sin-proyecto` para las tareas sin proyecto. */
  proyecto: z.union([z.literal('sin-proyecto'), idSchema]).optional(),
  tocaRiego: booleanoDeTexto.optional(),
  /** Id de una tarea madre: solo sus subtareas (ADR-0018). Se combina con los demás filtros (por defecto, solo abiertas). */
  padre: idSchema.optional(),
  /**
   * `true`: solo tareas bloqueadas (con algún bloqueante abierto y no eliminado); `false`: solo las no bloqueadas
   * (ADR-0018). Se combina con los demás filtros.
   */
  bloqueada: booleanoDeTexto.optional(),
  /**
   * `true`: solo tareas atrasadas (fecha límite anterior a hoy en Chile y abiertas; ver `estaAtrasada`); `false`: solo
   * las no atrasadas (sin fecha, con fecha de hoy en adelante, o cerradas). Se combina con los demás filtros.
   */
  atrasada: booleanoDeTexto.optional(),
  /**
   * Por defecto la lista trae solo tareas abiertas; `incluirCerradas=true` agrega las hechas y las descartadas.
   * Si viene `estado`, manda `estado` (p. ej. `estado=HECHO,DESCARTADA` = solo cerradas). Reemplaza a
   * `incluirHechas` (cambio incompatible: ese parámetro ya no existe y se ignora).
   */
  incluirCerradas: booleanoDeTexto.default(false),
  limite: z.coerce.number().int().min(1).max(1000).default(500),
  desplazamiento: z.coerce.number().int().min(0).default(0),
});
export type FiltrosTareas = z.infer<typeof filtrosTareasSchema>;

/* ---------------------------- Respuestas ---------------------------- */

/** Un movimiento del historial de una tarea. */
export const eventoTareaSchema = z.object({
  id: z.number().int(),
  tipo: z.enum(TIPOS_EVENTO),
  /** Valor anterior (en cambios de estado, prioridad o responsable). */
  de: z.string().nullable(),
  /** Valor nuevo. */
  a: z.string().nullable(),
  /** Texto libre: el de una nota, o un resumen de lo editado. */
  texto: z.string(),
  autor: usuarioResumenSchema.nullable(),
  creadoEn: fechaIsoSchema,
});
export type EventoTarea = z.infer<typeof eventoTareaSchema>;

/** Referencia corta a otra tarea (madre, subtarea o bloqueo): lo justo para mostrarla y enlazarla. */
export const tareaRelacionadaSchema = z.object({
  id: z.number().int(),
  /** Número legible («CDT-12»). */
  numero: z.number().int(),
  titulo: z.string(),
  estado: z.enum(ESTADOS),
});
export type TareaRelacionada = z.infer<typeof tareaRelacionadaSchema>;

/** Avance de una madre: subtareas hechas sobre el total (las descartadas no cuentan; ver `progresoSubtareas`). */
export const progresoSubtareasSchema = z.object({
  total: z.number().int(),
  hechas: z.number().int(),
});
export type ProgresoSubtareas = z.infer<typeof progresoSubtareasSchema>;

/** Tarea tal como se muestra en el tablero y en la lista. */
export const tareaSchema = z.object({
  id: z.number().int(),
  /** Número legible: la web lo muestra como «CDT-12». */
  numero: z.number().int(),
  titulo: z.string(),
  descripcion: z.string(),
  criterio: z.string(),
  tipo: z.enum(TIPOS),
  prioridad: z.enum(PRIORIDADES),
  esfuerzo: z.enum(ESFUERZOS),
  estado: z.enum(ESTADOS),
  responsable: usuarioResumenSchema.nullable(),
  pedidoPor: z.string(),
  tocaRiego: z.boolean(),
  requiereProduccion: z.boolean(),
  detalleProduccion: z.string(),
  enlace: z.string(),
  /** Proyecto de la tarea; `null` si no tiene. Campo aditivo: no rompe a quien ya consume la API. */
  proyecto: proyectoResumenSchema.nullable(),
  /**
   * Quien creó la tarea; `null` si no se conoce (autor borrado o sin autor en el historial).
   * Solo esa persona y los administradores editan el requerimiento (ver `puedeEditarRequerimiento`).
   * Campo aditivo.
   */
  creadaPor: usuarioResumenSchema.nullable(),
  /** Cantidad de adjuntos no eliminados de la tarea (la web muestra un clip). Campo aditivo. */
  adjuntos: z.number().int(),
  creadoEn: fechaIsoSchema,
  actualizadoEn: fechaIsoSchema,
  /** Cuándo se cerró (hecha o descartada); `null` si está abierta. */
  cerradaEn: fechaIsoSchema.nullable(),
  /**
   * Motivo del descarte; `null` salvo en tareas `DESCARTADA`. `duplicadaDe` es el número de la tarea original.
   * Campo aditivo.
   */
  descarte: z
    .object({
      motivo: z.enum(MOTIVOS_DESCARTE),
      detalle: z.string(),
      duplicadaDe: z.number().int().nullable(),
    })
    .nullable(),
  /** Tarea madre si esta es subtarea; `null` si no (ADR-0018). Campo aditivo. */
  padre: tareaRelacionadaSchema.nullable(),
  /** Avance de sus subtareas; `null` si no tiene subtareas (no eliminadas). Campo aditivo. */
  progresoSubtareas: progresoSubtareasSchema.nullable(),
  /**
   * Tareas que esta espera (ADR-0018), por número. Trae las no eliminadas, también las cerradas (que ya no bloquean:
   * la web las muestra tachadas). Campo aditivo.
   */
  bloqueadaPor: z.array(tareaRelacionadaSchema),
  /** `true` si algún bloqueante sigue abierto (ver `estaBloqueada`). Campo aditivo. */
  bloqueada: z.boolean(),
  /**
   * Fecha límite `AAAA-MM-DD` (día, sin hora ni zona), o `null` si no tiene. Para saber si está atrasada usa
   * `estaAtrasada(tarea, hoyEnChile())`. Campo aditivo.
   */
  fechaLimite: z.string().nullable(),
});
export type Tarea = z.infer<typeof tareaSchema>;

/** Tarea con su historial completo (para el panel de detalle). */
export const tareaDetalleSchema = tareaSchema.extend({
  eventos: z.array(eventoTareaSchema),
  /** Subtareas no eliminadas (abiertas y cerradas), por número. Vacío si no es madre. Campo aditivo. */
  subtareas: z.array(tareaRelacionadaSchema),
  /** Tareas no eliminadas que esperan a esta (abiertas y cerradas), por número. Campo aditivo. */
  bloqueaA: z.array(tareaRelacionadaSchema),
});
export type TareaDetalle = z.infer<typeof tareaDetalleSchema>;

export const listaTareasSchema = z.object({
  items: z.array(tareaSchema),
  /** Total que cumple los filtros, aunque `items` esté limitado por `limite`. */
  total: z.number().int(),
});
export type ListaTareas = z.infer<typeof listaTareasSchema>;

/** Resultado de crear, editar o mover: la tarea y avisos para la persona (no son errores). */
export const resultadoTareaSchema = z.object({
  tarea: tareaDetalleSchema,
  advertencias: z.array(z.string()),
});
export type ResultadoTarea = z.infer<typeof resultadoTareaSchema>;
